package es.subastia.auctionlist

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyGridState
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.ArrowUpward
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.FavoriteBorder
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.AssistChip
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import coil3.compose.AsyncImage
import es.subastia.domain.model.AuctionProcedureType
import es.subastia.navigation.NavRoute
import es.subastia.navigation.Navigator
import es.subastia.ui.components.RichAuctionCard
import es.subastia.ui.model.AuctionDatesUiModel
import es.subastia.ui.model.AuctionUiModel
import es.subastia.utils.formatMoney
import es.subastia.utils.isToday
import es.subastia.utils.toUserFriendlyDate
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.btn_close
import subastia.composeapp.generated.resources.btn_filter
import subastia.composeapp.generated.resources.btn_remove_filter
import subastia.composeapp.generated.resources.desc_filter_icon
import subastia.composeapp.generated.resources.desc_save_search
import subastia.composeapp.generated.resources.desc_splash_screen_logo
import subastia.composeapp.generated.resources.error_loading
import subastia.composeapp.generated.resources.ic_subastia
import subastia.composeapp.generated.resources.label_results_count
import subastia.composeapp.generated.resources.search_placeholder
import subastia.composeapp.generated.resources.sort_newest
import subastia.composeapp.generated.resources.sort_oldest

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuctionListScreen(
    navigator: Navigator,
    params: NavRoute.AuctionList = NavRoute.AuctionList(),
) {
    val viewModel: AuctionListViewModel = koinViewModel()

    androidx.compose.runtime.LaunchedEffect(params) {
        if (params.query != null || params.province != null || params.type != null || params.procedureType != null) {
            viewModel.loadSearchFromParams(params)
        }
    }

    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val searchQuery by viewModel.currentSearchQuery.collectAsStateWithLifecycle()
    val isRefreshing by viewModel.isRefreshing.collectAsStateWithLifecycle()
    val isSearchSaved by viewModel.isSearchSaved.collectAsStateWithLifecycle()

    // Hoisted state to survive navigation/recomposition
    val listState = rememberLazyGridState(
        initialFirstVisibleItemIndex = viewModel.scrollIndex,
        initialFirstVisibleItemScrollOffset = viewModel.scrollOffset
    )

    androidx.compose.runtime.DisposableEffect(Unit) {
        onDispose {
            viewModel.saveScrollPosition(
                listState.firstVisibleItemIndex,
                listState.firstVisibleItemScrollOffset
            )
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
    ) { padding ->
        Box(
            modifier = Modifier
                .padding(top = padding.calculateTopPadding())
                .fillMaxSize(),
        ) {
            when (val stateValue = state) {
                is AuctionUiState.Error -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = stringResource(Res.string.error_loading, stateValue.message),
                            color = MaterialTheme.colorScheme.error,
                        )
                    }
                }

                AuctionUiState.Loading -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                    }
                }

                is AuctionUiState.Success -> {
                    AuctionListSuccessContent(
                        stateValue = stateValue,
                        listState = listState,
                        isRefreshing = isRefreshing,
                        onRefresh = viewModel::refresh,
                        onFilterClicked = { navigator.navigateTo(NavRoute.AuctionFilter) },
                        onDetailClicked = { navigator.navigateTo(NavRoute.AuctionDetail(it.auctionId)) },
                        onSortClicked = viewModel::toggleSortOrder,
                        onSearchQueryChanged = viewModel::onSearchQueryChanged,
                        onRemoveProvince = viewModel::onRemoveProvince,
                        onRemoveType = viewModel::onRemoveType,
                        onRemoveProcedureType = viewModel::onRemoveProcedureType,
                        currentSearchQuery = searchQuery,
                        onSaveSearch = viewModel::toggleSavedSearch,
                        isSearchSaved = isSearchSaved,
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuctionListSuccessContent(
    stateValue: AuctionUiState.Success,
    listState: LazyGridState,
    isRefreshing: Boolean,
    onRefresh: () -> Unit,
    onFilterClicked: () -> Unit,
    onSortClicked: () -> Unit,
    onSearchQueryChanged: (String) -> Unit,
    onRemoveProvince: () -> Unit,
    onRemoveType: () -> Unit,
    onRemoveProcedureType: () -> Unit,
    currentSearchQuery: String,
    onDetailClicked: (AuctionUiModel) -> Unit,
    onSaveSearch: () -> Unit,
    isSearchSaved: Boolean,
) {
    Column(Modifier.fillMaxSize()) {
        // Search Bar Row
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Image(
                painter = painterResource(Res.drawable.ic_subastia),
                contentDescription = stringResource(Res.string.desc_splash_screen_logo),
                modifier = Modifier.size(40.dp),
            )

            OutlinedTextField(
                value = currentSearchQuery,
                onValueChange = onSearchQueryChanged,
                modifier = Modifier.weight(1f),
                placeholder = { Text(stringResource(Res.string.search_placeholder)) },
                leadingIcon = null,
                trailingIcon = {
                    Row(
                        modifier = Modifier.padding(end = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (currentSearchQuery.isNotEmpty()) {
                            IconButton(
                                onClick = { onSearchQueryChanged("") },
                                modifier = Modifier.pointerHoverIcon(PointerIcon.Hand)
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Close,
                                    contentDescription = stringResource(Res.string.btn_close),
                                )
                            }
                        }

                        if (stateValue.isLoggedIn) {
                            IconButton(
                                onClick = onSaveSearch,
                                modifier = Modifier.pointerHoverIcon(PointerIcon.Hand)
                            ) {
                                Icon(
                                    imageVector = if (isSearchSaved) Icons.Default.Favorite else Icons.Filled.FavoriteBorder,
                                    contentDescription = stringResource(Res.string.desc_save_search),
                                    tint = if (isSearchSaved) MaterialTheme.colorScheme.primary else LocalContentColor.current
                                )
                            }
                        }
                    }
                },
                shape = RoundedCornerShape(12.dp),
                singleLine = true,
            )
        }

        // Filter Chips Row
        LazyRow(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            item {
                FilterChip(
                    selected = true, // Always "selected" as action
                    onClick = onFilterClicked,
                    label = { Text(stringResource(Res.string.btn_filter)) },
                    leadingIcon = { Icon(Icons.Default.FilterList, stringResource(Res.string.desc_filter_icon)) },
                    colors = FilterChipDefaults.filterChipColors(
                        selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
                        selectedLabelColor = MaterialTheme.colorScheme.onPrimaryContainer,
                        selectedLeadingIconColor = MaterialTheme.colorScheme.onPrimaryContainer,
                    ),
                )
            }

            item {
                FilterChip(
                    selected = false,
                    onClick = onSortClicked,
                    label = {
                        Text(
                            if (stateValue.sortOrder == SortOrder.NEWEST) {
                                stringResource(Res.string.sort_newest)
                            } else {
                                stringResource(Res.string.sort_oldest)
                            },
                        )
                    },
                    leadingIcon = {
                        Icon(if (stateValue.sortOrder == SortOrder.NEWEST) Icons.Default.ArrowDownward else Icons.Default.ArrowUpward, null)
                    },
                    colors = FilterChipDefaults.filterChipColors(),
                )
            }

            stateValue.selectedProvince?.let { province ->
                item {
                    AssistChip(
                        onClick = onFilterClicked,
                        label = { Text(province) },
                        leadingIcon = { Icon(Icons.Default.LocationOn, null, modifier = Modifier.size(16.dp)) },
                        trailingIcon = {
                            IconButton(onClick = onRemoveProvince, modifier = Modifier.size(20.dp)) {
                                Icon(
                                    Icons.Default.Close,
                                    stringResource(Res.string.btn_remove_filter),
                                    modifier = Modifier.size(16.dp),
                                )
                            }
                        },
                    )
                }
            }

            stateValue.selectedType?.let { type ->
                item {
                    AssistChip(
                        onClick = onFilterClicked,
                        label = { Text(type) },
                        trailingIcon = {
                            IconButton(onClick = onRemoveType, modifier = Modifier.size(20.dp)) {
                                Icon(
                                    Icons.Default.Close,
                                    stringResource(Res.string.btn_remove_filter),
                                    modifier = Modifier.size(16.dp),
                                )
                            }
                        },
                    )
                }
            }

            stateValue.selectedProcedureType?.let { procType ->
                item {
                    AssistChip(
                        onClick = onFilterClicked,
                        label = { Text(procType.label) },
                        trailingIcon = {
                            IconButton(onClick = onRemoveProcedureType, modifier = Modifier.size(20.dp)) {
                                Icon(
                                    Icons.Default.Close,
                                    stringResource(Res.string.btn_remove_filter),
                                    modifier = Modifier.size(16.dp),
                                )
                            }
                        },
                    )
                }
            }
        }

        Text(
            text = stringResource(Res.string.label_results_count, stateValue.list.size),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
        )

        // Adaptive Grid/List
        BoxWithConstraints(
            Modifier
                .weight(1f)
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
        ) {
            val minWidth = 320.dp // Width for each card
            val columns = if (maxWidth > 600.dp) GridCells.Adaptive(minWidth) else GridCells.Fixed(1)

            androidx.compose.material3.pulltorefresh.PullToRefreshBox(
                isRefreshing = isRefreshing,
                onRefresh = onRefresh,
                modifier = Modifier.fillMaxSize(),
            ) {
                LazyVerticalGrid(
                    columns = columns,
                    state = listState,
                    contentPadding = PaddingValues(0.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    modifier = Modifier.fillMaxSize(),
                ) {
                    items(stateValue.list, key = { it.auctionId }) { auction ->
                        RichAuctionCard(
                            auction = auction,
                            onClick = { onDetailClicked(auction) },
                        )
                    }
                }
            }
        }
    }
}

// --- Previews ---

private val SampleAuction = AuctionUiModel(
    auctionId = "123",
    title = "Vivienda en Calle Mayor, Madrid",
    currentPrice = 150000.0,
    auctionValue = 200000.0,
    appraisalValue = 220000.0,
    depositAmount = 10000.0,
    claimedAmount = 50000.0,
    bidStep = 1000.0,
    dates = AuctionDatesUiModel("2023-01-01", "2023-12-31"),
    city = "Madrid",
    province = "Madrid",
    status = "ACTIVE",
    type = "Inmuebles",
    hasLots = false,
    address = "Calle Mayor 123",
    model = null,
    portalUrl = "https://subastas.boe.es/detalleSubasta.php?idSub=SUB-JA-2025-249439",
    procedureType = AuctionProcedureType.JUDICIAL_APREMIO,
)

@Preview
@Composable
private fun AuctionCardPreview() {
    MaterialTheme {
        RichAuctionCard(auction = SampleAuction, onClick = {})
    }
}

@Preview
@Composable
private fun AuctionListScreenPreview() {
    MaterialTheme {
        AuctionListSuccessContent(
            stateValue = AuctionUiState.Success(
                list = listOf(SampleAuction, SampleAuction.copy(auctionId = "456", title = "BMW X5")),
                sortOrder = SortOrder.NEWEST,
                selectedProvince = null,
                selectedType = null,
            ),
            listState = rememberLazyGridState(),
            isRefreshing = false,
            onRefresh = {},
            onFilterClicked = {},
            onSortClicked = {},
            onSearchQueryChanged = {},
            onRemoveProvince = {},
            onRemoveType = {},
            onRemoveProcedureType = {},
            currentSearchQuery = "BMW",
            onDetailClicked = {},
            onSaveSearch = {},
            isSearchSaved = false,
        )
    }
}
